/**
 * Parses URL path parameters from a URL based on a URL pattern.
 *
 * @param pathPattern The path pattern to parse.
 * @param path The url pathname to parse.
 * @returns An object containing the path parameters, or null if the URL does not match the pattern.
 *
 * @example parseUrlPathParams(
 *  "/bootcamp/:subtopic/:key/:moduleKey/:level/:action",
 *  "/bootcamp/numbers-and-quantities/fraction/2/5/how"
 * ) => {
 *      moduleKey: "2",
 *      level: "5",
 *      action: "how",
 *      key: "fraction",
 *      subtopic: "numbers-and-quantities"
 * }
 */

export function parseUrlPathParams(pathPattern: string, path: string): Record<string, string> | null {
  try {
    // Create a regular expression from the URL pattern
    const pattern = pathPattern
      .replace(/:[^\s/]+/g, '([^/]+)') // Replace :param with a capture group
      .replace(/\//g, '\\/') // Escape forward slashes for regex

    const regex = new RegExp(`^${pattern}$`)
    const match = path.match(regex)

    if (!match) return null

    const keys = (pathPattern.match(/:[^\s/]+/g) || []).map((key) => key.slice(1))
    const values = match.slice(1)

    return keys.reduce((acc, key, index) => {
      acc[key] = values[index]
      return acc
    }, {} as Record<string, string>)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Invalid path:', error)
    return null
  }
}
